/**
 * Firebase Synchronization Service
 * Handles tournament data persistence with Firestore
 */
import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocFromServer,
  getDocs,
  deleteDoc,
  updateDoc,
  query,
  where,
  limit,
  serverTimestamp,
  addDoc
} from 'firebase/firestore';
import { db, COLLECTIONS } from '../firebase';

class FirebaseSyncService {
  constructor() {
    this.currentTournamentId = null;
    this.isCreatingTournament = false;
  }

  /**
   * Save the current tournament to Firestore
   * @param {Object} tournamentState - Complete tournament state from TournamentContext
   * @param {string} userId - User email
   * @param {string|null} screenName - Current screen name
   * @returns {Promise<boolean>} Success status
   */
  async saveTournament(tournamentState, userId, screenName = null) {
    if (!userId) {
      console.warn('❌ Cannot save tournament: user not signed in');
      return false;
    }

    if (!this.currentTournamentId) {
      console.warn('❌ Cannot save tournament: no tournament ID');
      return false;
    }

    try {
      // Clean state for Firestore
      const cleanedState = this.cleanStateForFirestore(tournamentState);

      const tournamentData = {
        userId: userId,
        mode: 'montante',
        updatedAt: serverTimestamp(),
        currentScreen: screenName || 'config',
        state: cleanedState
      };

      const docRef = doc(db, COLLECTIONS.TOURNAMENTS, this.currentTournamentId);
      await setDoc(docRef, tournamentData, { merge: true });

      return true;

    } catch (error) {
      console.error('❌ Error saving tournament:', error);
      throw error;
    }
  }

  /**
   * Load a tournament from Firestore
   * @param {string} tournamentId - Tournament document ID
   * @param {string} userId - User email
   * @returns {Promise<Object|null>} Tournament data or null
   */
  async loadTournament(tournamentId, userId) {
    if (!userId) {
      console.warn('❌ Cannot load tournament: user not signed in');
      return null;
    }

    try {
      const docRef = doc(db, COLLECTIONS.TOURNAMENTS, tournamentId);
      const docSnap = await getDocFromServer(docRef);

      if (!docSnap.exists()) {
        throw new Error('Tournament not found');
      }

      const data = docSnap.data();

      // Verify ownership
      if (data.userId !== userId) {
        throw new Error('Unauthorized access to tournament');
      }

      // Save tournament ID
      this.currentTournamentId = tournamentId;
      localStorage.setItem('currentTournamentId', tournamentId);

      console.log(`✅ Tournament loaded from Firestore:`, data.state?.config?.tournamentName || 'Sans nom');
      return { id: tournamentId, ...data };

    } catch (error) {
      console.error('❌ Error loading tournament:', error);
      throw error;
    }
  }

  /**
   * Create a new tournament in Firestore
   * @param {Object} tournamentState - Initial tournament state
   * @param {string} userId - User email
   * @param {string|null} name - Tournament name (auto-generated if null)
   * @returns {Promise<string|null>} Tournament ID or null on error
   */
  async createNewTournament(tournamentState, userId, name = null) {
    if (!userId) {
      console.warn('❌ Cannot create tournament: user not signed in');
      return null;
    }

    // Prevent parallel tournament creation
    if (this.isCreatingTournament) {
      console.warn('⚠️ Tournament creation already in progress');
      return null;
    }

    this.isCreatingTournament = true;

    try {
      const tournamentName = name || this.generateTournamentName();
      const cleanedState = this.cleanStateForFirestore(tournamentState);

      // Ensure tournamentName is in the config
      if (!cleanedState.config) {
        cleanedState.config = {};
      }
      if (!cleanedState.config.tournamentName) {
        cleanedState.config.tournamentName = tournamentName;
      }

      // Always create new courts with default names for a new tournament
      const courtCount = cleanedState.config.courtCount || 2;
      cleanedState.courts = [];
      for (let i = 0; i < courtCount; i++) {
        cleanedState.courts.push({
          id: i,
          name: `Terrain ${i + 1}`
        });
      }

      const tournamentData = {
        userId: userId,
        mode: 'montante',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        currentScreen: 'config',
        state: cleanedState
      };

      const docRef = await addDoc(collection(db, COLLECTIONS.TOURNAMENTS), tournamentData);

      this.currentTournamentId = docRef.id;
      localStorage.setItem('currentTournamentId', docRef.id);

      console.log(`✅ New tournament created:`, tournamentName);
      return docRef.id;

    } catch (error) {
      console.error('❌ Error creating tournament:', error);
      throw error;
    } finally {
      this.isCreatingTournament = false;
    }
  }

  /**
   * List all tournaments for a user
   * @param {string} userId - User email
   * @returns {Promise<Array>} Array of tournament documents
   */
  async listUserTournaments(userId) {
    if (!userId) {
      console.warn('❌ Cannot list tournaments: user not signed in');
      return [];
    }

    try {
      const q = query(
        collection(db, COLLECTIONS.TOURNAMENTS),
        where('userId', '==', userId),
        limit(50)
      );

      const querySnapshot = await getDocs(q);

      const tournaments = [];
      querySnapshot.forEach((doc) => {
        tournaments.push({
          id: doc.id,
          ...doc.data()
        });
      });

      // Sort by updatedAt descending (client-side)
      tournaments.sort((a, b) => {
        const timeA = a.updatedAt?.toDate?.()?.getTime() || 0;
        const timeB = b.updatedAt?.toDate?.()?.getTime() || 0;
        return timeB - timeA;
      });

      console.log(`✅ Found ${tournaments.length} tournaments`);
      return tournaments;

    } catch (error) {
      console.error('❌ Error listing tournaments:', error);
      throw error;
    }
  }

  /**
   * Delete a tournament from Firestore
   * @param {string} tournamentId - Tournament document ID
   * @param {string} userId - User email (for verification)
   * @returns {Promise<boolean>} Success status
   */
  async deleteTournament(tournamentId, userId) {
    if (!userId) {
      console.warn('❌ Cannot delete tournament: user not signed in');
      return false;
    }

    try {
      // Verify ownership before deleting
      const docRef = doc(db, COLLECTIONS.TOURNAMENTS, tournamentId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        throw new Error('Tournament not found');
      }

      const data = docSnap.data();
      if (data.userId !== userId) {
        throw new Error('Unauthorized: cannot delete this tournament');
      }

      // Delete the document
      await deleteDoc(docRef);

      // If deleting current tournament, clear local reference
      if (this.currentTournamentId === tournamentId) {
        this.currentTournamentId = null;
        localStorage.removeItem('currentTournamentId');
      }

      console.log('✅ Tournament deleted:', tournamentId);
      return true;

    } catch (error) {
      console.error('❌ Error deleting tournament:', error);
      throw error;
    }
  }

  /**
   * Rename a tournament
   * @param {string} tournamentId - Tournament document ID
   * @param {string} newName - New tournament name
   * @param {string} userId - User email (for verification)
   * @returns {Promise<boolean>} Success status
   */
  async renameTournament(tournamentId, newName, userId) {
    if (!userId) {
      console.warn('❌ Cannot rename tournament: user not signed in');
      return false;
    }

    if (!newName || newName.trim() === '') {
      throw new Error('Le nom ne peut pas être vide');
    }

    try {
      // Verify ownership before renaming
      const docRef = doc(db, COLLECTIONS.TOURNAMENTS, tournamentId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        throw new Error('Tournament not found');
      }

      const data = docSnap.data();
      if (data.userId !== userId) {
        throw new Error('Unauthorized: cannot rename this tournament');
      }

      // Update only state.config.tournamentName
      await updateDoc(docRef, {
        'state.config.tournamentName': newName.trim(),
        updatedAt: serverTimestamp()
      });

      console.log('✅ Tournament renamed:', newName);
      return true;

    } catch (error) {
      console.error('❌ Error renaming tournament:', error);
      throw error;
    }
  }

  /**
   * Duplicate a tournament (copy config and players only)
   * @param {string} tournamentId - Tournament document ID to duplicate
   * @param {string} userId - User email
   * @returns {Promise<string|null>} New tournament ID or null on error
   */
  async duplicateTournament(tournamentId, userId) {
    if (!userId) {
      console.warn('❌ Cannot duplicate tournament: user not signed in');
      return null;
    }

    try {
      // Load source tournament
      const sourceDocRef = doc(db, COLLECTIONS.TOURNAMENTS, tournamentId);
      const sourceDoc = await getDoc(sourceDocRef);

      if (!sourceDoc.exists()) {
        throw new Error('Tournament not found');
      }

      const sourceData = sourceDoc.data();

      // Verify ownership
      if (sourceData.userId !== userId) {
        throw new Error('Unauthorized access to tournament');
      }

      // Create new tournament with copied config and players
      const sourceTournamentName = sourceData.state?.config?.tournamentName || 'Tournoi';
      const newTournamentName = `${sourceTournamentName} (Copie)`;

      // Copy or create courts
      let courts = sourceData.state.courts || [];
      if (courts.length === 0) {
        const courtCount = sourceData.state.config.courtCount || 2;
        courts = [];
        for (let i = 0; i < courtCount; i++) {
          courts.push({
            id: i,
            name: `Terrain ${i + 1}`
          });
        }
      }

      const newState = {
        config: {
          ...sourceData.state.config,
          tournamentName: newTournamentName
        },
        players: sourceData.state.players,
        courts: courts,
        schedule: { rounds: [], hasRepetitions: false, warnings: [] },
        currentRound: 0,
        scheduleGenerated: false,
        tournamentStarted: false,
        tournamentFinished: false,
        startTime: null
      };

      const newTournamentData = {
        userId: userId,
        mode: 'montante',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        currentScreen: 'config',
        state: newState
      };

      const newDocRef = await addDoc(collection(db, COLLECTIONS.TOURNAMENTS), newTournamentData);

      console.log('✅ Tournament duplicated:', newDocRef.id);
      return newDocRef.id;

    } catch (error) {
      console.error('❌ Error duplicating tournament:', error);
      throw error;
    }
  }

  /**
   * Load the most recent tournament
   * @param {string} userId - User email
   * @returns {Promise<Object|null>} Tournament data or null
   */
  async loadLastTournament(userId) {
    if (!userId) {
      return null;
    }

    try {
      const tournaments = await this.listUserTournaments(userId);

      if (tournaments.length === 0) {
        console.log('ℹ️ No tournaments found');
        return null;
      }

      // Most recent is first (already sorted in listUserTournaments)
      const mostRecent = tournaments[0];
      return await this.loadTournament(mostRecent.id, userId);

    } catch (error) {
      console.error('❌ Error loading last tournament:', error);
      return null;
    }
  }

  /**
   * Clean state object for Firestore (remove invalid dates, functions, etc.)
   * @param {Object} state - Tournament state to clean
   * @returns {Object} Cleaned state
   */
  cleanStateForFirestore(state) {
    // Deep clone to avoid modifying original
    const cleaned = JSON.parse(JSON.stringify(state, (key, value) => {
      // Remove functions
      if (typeof value === 'function') {
        return undefined;
      }
      // Handle Date objects
      if (value instanceof Date) {
        return value.toISOString();
      }
      return value;
    }));

    // For each match, keep only detailedScore.sets (to restore in-progress scores on reload)
    // playerResults is kept for validated matches; detailedScore.sets for non-validated ones
    if (cleaned.schedule && cleaned.schedule.rounds) {
      cleaned.schedule.rounds.forEach(round => {
        if (round.matches) {
          round.matches.forEach(match => {
            const sets = match.detailedScore?.sets;
            const hasSets = Array.isArray(sets) && sets.length > 0 && sets.some(s => s && (s.team1 > 0 || s.team2 > 0));
            if (hasSets) {
              match.detailedScore = { sets };
            } else {
              delete match.detailedScore;
            }
          });
        }
      });
    }

    return cleaned;
  }

  /**
   * Generate a tournament name based on current date
   * @returns {string} Generated tournament name
   */
  generateTournamentName() {
    const now = new Date();
    const day = String(now.getDate()).padStart(2, '0');
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const year = now.getFullYear();
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');

    return `Tournoi ${day}/${month}/${year} ${hours}:${minutes}`;
  }

  /**
   * Mark planning as generated in Firestore
   * @param {string} userId - User email
   * @returns {Promise<boolean>} Success status
   */
  async markPlanningAsGenerated(userId) {
    if (!userId) {
      console.warn('❌ Cannot mark planning: user not signed in');
      return false;
    }

    if (!this.currentTournamentId) {
      console.warn('❌ Cannot mark planning: no tournament ID');
      return false;
    }

    try {
      const docRef = doc(db, COLLECTIONS.TOURNAMENTS, this.currentTournamentId);
      await updateDoc(docRef, {
        planning: true,
        planningDate: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      console.log('✅ Planning marked as generated in Firestore');
      return true;

    } catch (error) {
      console.error('❌ Error marking planning as generated:', error);
      throw error;
    }
  }

  /**
   * Clear current tournament reference
   */
  clearCurrentTournament() {
    this.currentTournamentId = null;
    localStorage.removeItem('currentTournamentId');
  }
}

// Export singleton instance
const firebaseSyncService = new FirebaseSyncService();
export default firebaseSyncService;
